import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSiteContent } from "@/context/SiteContentContext";
import { SECTIONS } from "@/lib/sections";
import { toast } from "sonner";
import { ImageIcon, Type, RotateCcw, Save, History } from "lucide-react";

/**
 * Editor's Workshop — inline editor for site content overrides.
 * Renders a list of editable text + image fields grouped into sections.
 * Saves to /api/site-content/:key on blur or explicit Save.
 */

// Field schema. Each field has a key, label, kind (text|textarea|image), and a default.
const FIELD_GROUPS = [
  {
    title: "Landing page",
    overline: "Hero · CTAs · editor quote",
    fields: [
      { key: "landing.hero.logo", label: "Hero logo", kind: "image",
        default: "/brand/le-salon-full.png",
        hint: "The main Le Salon logo image at the top of the home page." },
      { key: "landing.hero.tagline", label: "Hero tagline", kind: "textarea",
        default: "A peaceful retreat where beauty, creativity, meaningful conversation and genuine connection are celebrated." },
      { key: "landing.hero.image", label: "Hero image", kind: "image",
        default: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1600&q=85" },
      { key: "landing.cta.join", label: "Primary CTA", kind: "text", default: "Step Inside" },
      { key: "landing.cta.tagline", label: "Below-CTA note", kind: "text",
        default: "Free for now · subscriber privileges soon" },
      { key: "landing.editor.quote", label: "Editor's quote", kind: "textarea",
        default: "The French don't chase beauty. They make small, daily appointments with it." },
    ],
  },
  {
    title: "Sign-in page",
    overline: "The welcome-back page at /login",
    fields: [
      { key: "signin.overline", label: "Overline (small caps)", kind: "text", default: "Welcome back" },
      { key: "signin.title", label: "Title", kind: "text", default: "Sign in to the salon." },
      { key: "signin.hero.quote", label: "Side quote", kind: "textarea",
        default: '"Bonjour, encore — we saved your chair by the window."' },
      { key: "signin.hero.image", label: "Side image", kind: "image",
        default: "https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=1200&q=85" },
    ],
  },
  {
    title: "Register page",
    overline: "The join-us page at /register",
    fields: [
      { key: "register.overline", label: "Overline (small caps)", kind: "text", default: "Become a member" },
      { key: "register.title", label: "Title", kind: "text", default: "Reserve your seat at the table." },
      { key: "register.subtitle", label: "Subtitle line", kind: "textarea",
        default: "Free to join. Open to anyone who loves living beautifully." },
      { key: "register.hero.quote", label: "Side quote", kind: "textarea",
        default: '"There is room at the table for one more."' },
      { key: "register.hero.image", label: "Side image", kind: "image",
        default: "https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=1200&q=85" },
    ],
  },
  {
    title: "Feed / Salon home",
    overline: "The top of the logged-in home",
    fields: [
      { key: "feed.hero.overline", label: "Overline (small caps)", kind: "text", default: "Today in the salon" },
      { key: "feed.hero.body", label: "Sub-heading text", kind: "textarea",
        default: "A few quiet, beautiful things our members shared today." },
    ],
  },
  {
    title: "Rooms landing",
    overline: "The header of the /rooms page",
    fields: [
      { key: "rooms.hero.overline", label: "Overline (small caps)", kind: "text", default: "The Salon · Rooms" },
      { key: "rooms.hero.title", label: "Title (use line breaks for two lines)", kind: "textarea",
        default: "Seven rooms.\nSeven conversations." },
      { key: "rooms.hero.body", label: "Sub-heading text", kind: "textarea",
        default: "Each is its own small magazine — with its own editor's pick at the top, and a quiet, growing collection of posts below." },
    ],
  },
  {
    title: "Rooms",
    overline: "Each room's title, overline, description, and cover image",
    fields: SECTIONS.flatMap((s) => [
      { key: `rooms.${s.slug}.label`, label: `${s.label} · title`, kind: "text", default: s.label },
      { key: `rooms.${s.slug}.overline`, label: `${s.label} · overline`, kind: "text", default: s.overline },
      { key: `rooms.${s.slug}.description`, label: `${s.label} · description`, kind: "textarea", default: s.description },
      { key: `rooms.${s.slug}.image`, label: `${s.label} · cover image`, kind: "image", default: s.image },
    ]),
  },
  {
    title: "About page",
    overline: "The /about page — text body + optional cover image",
    fields: [
      { key: "about.hero.image", label: "Cover image (optional)", kind: "image",
        default: "",
        hint: "A single hero image shown at the top of the About page. Leave empty for a text-only page." },
      { key: "about.body", label: "About body", kind: "textarea",
        default: "Le Salon is a quiet gathering place for women who love living beautifully — slowly, intentionally, and in the French style.\n\nHere we share letters, lives, kitchen mornings, small pleasures, and the occasional live broadcast. It is a magazine, a journal, and a community — all in one hushed doorway.\n\nFounded by Mimi as a companion to Belle Inspiration Magazine and Joie de Vivre TV." },
    ],
  },
  {
    title: "Legal & Community",
    overline: "Community Guidelines · Terms · Privacy — editable text",
    fields: [
      { key: "legal.guidelines.body", label: "Community Guidelines body", kind: "textarea",
        default: "In this salon we celebrate slow reading, careful writing, gentle wit, and the kind of honesty that makes friendship possible. We don't tolerate cruelty, self-promotion, spam, harassment, or drama. Mimi is the editor and has final say. If you have a concern, use the report button on any member's profile — it comes directly and privately to her.\n\nMembers who repeatedly disturb the calm may be quietly removed. The goal is not to police, but to protect what makes this place worth returning to." },
      { key: "legal.terms.body", label: "Terms of Service body", kind: "textarea",
        default: "By using Le Salon, you agree to these terms. You must be 16 or older to hold an account. You own the content you post; you grant Le Salon a license to display it within the site. You agree not to post illegal content, impersonate others, spam, or harass. We may remove content or suspend accounts that violate these terms. This service is provided as-is. This is a placeholder — please have a lawyer review and refine before public launch." },
      { key: "legal.privacy.body", label: "Privacy Policy body", kind: "textarea",
        default: "Le Salon collects only what's needed to run the service: your email, name, photos, messages, and browser info. We store this on MongoDB Atlas (encrypted at rest), use Cloudinary for photos, Mux for video, and Resend for email. All traffic is HTTPS-encrypted. Passwords are hashed with bcrypt. We do not sell your data, run ads, or share personal info with third parties. You can request deletion of your account and data anytime via Settings. This is a placeholder — please have a lawyer review before public launch." },
    ],
  },
];

const fmtAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const HistoryPopover = ({ fieldKey, onRestore, onClose }) => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let active = true;
    api.get(`/site-content/${encodeURIComponent(fieldKey)}/history`)
      .then(({ data }) => { if (active) setItems(data); })
      .catch(() => { if (active) setItems([]); });
    return () => { active = false; };
  }, [fieldKey]);

  return (
    <div
      data-testid={`workshop-history-${fieldKey}`}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "absolute",
        top: 30,
        right: 0,
        zIndex: 50,
        width: 340,
        maxHeight: 360,
        overflowY: "auto",
        background: "var(--surface)",
        border: "1px solid var(--border-default)",
        boxShadow: "0 12px 32px rgba(51,51,51,0.12)",
        padding: 16,
      }}
    >
      <div className="overline" style={{ color: "var(--brand-accent)", marginBottom: 10 }}>
        Version history
      </div>
      {items === null && (
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", color: "var(--text-muted)", fontSize: 13 }}>
          Loading…
        </div>
      )}
      {items && items.length === 0 && (
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", color: "var(--text-muted)", fontSize: 13 }}>
          No earlier versions yet. Edits made from here on will be remembered.
        </div>
      )}
      {items && items.map((h, i) => (
        <div
          key={i}
          style={{
            padding: "10px 0",
            borderBottom: i < items.length - 1 ? "1px solid var(--border-default)" : "none",
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: 10,
            alignItems: "flex-start",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "'Outfit', sans-serif", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>
              {fmtAgo(h.changed_at)}{h.changed_by?.display_name ? ` · ${h.changed_by.display_name}` : ""}
            </div>
            <div
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 13,
                color: "var(--text-primary)",
                lineHeight: 1.4,
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                wordBreak: "break-word",
              }}
              title={h.previous_value}
            >
              {h.previous_value
                ? (h.previous_value.startsWith("http") ? h.previous_value : `"${h.previous_value}"`)
                : <em style={{ color: "var(--text-muted)" }}>(empty / default)</em>}
            </div>
          </div>
          <button
            type="button"
            data-testid={`workshop-restore-${fieldKey}-${i}`}
            onClick={() => { onRestore(h.previous_value || ""); onClose(); }}
            className="btn-secondary"
            style={{ fontSize: 11, padding: "6px 10px" }}
          >
            Restore
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={onClose}
        className="overline"
        style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", marginTop: 14, fontSize: 11 }}
      >
        Close
      </button>
    </div>
  );
};

const HistoryButton = ({ fieldKey, onRestore }) => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        data-testid={`workshop-history-btn-${fieldKey}`}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        aria-label="Version history"
        title="Version history"
        style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 6 }}
      >
        <History size={14} />
      </button>
      {open && (
        <HistoryPopover
          fieldKey={fieldKey}
          onRestore={onRestore}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
};

const FieldRow = ({ field, value, onChange, onReset, busy }) => {
  const [local, setLocal] = useState(value ?? "");
  useEffect(() => setLocal(value ?? ""), [value]);

  const isOverridden = value !== undefined && value !== null && value !== "" && value !== field.default;
  const showValue = local || field.default;

  const handleBlur = () => {
    if (local !== (value ?? "")) onChange(field.key, local);
  };

  if (field.kind === "image") {
    return (
      <ImageField
        field={field}
        value={value}
        local={local}
        setLocal={setLocal}
        onChange={onChange}
        onReset={onReset}
        isOverridden={isOverridden}
        busy={busy}
      />
    );
  }

  const InputTag = field.kind === "textarea" ? "textarea" : "input";
  return (
    <div className="workshop-row" style={{ padding: "14px 0", borderBottom: "1px solid var(--border-default)" }}>
      <label className="workshop-label" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 15, color: "var(--text-primary)" }}>
        {field.label}
        {isOverridden && (
          <span style={{ display: "block", fontSize: 10, color: "var(--brand-accent)", fontFamily: "'Outfit', sans-serif", letterSpacing: "0.15em", textTransform: "uppercase", marginTop: 4 }}>
            Custom
          </span>
        )}
      </label>
      <div className="workshop-input-wrap">
        <InputTag
          data-testid={`workshop-${field.key}`}
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={handleBlur}
          placeholder={field.default}
          rows={field.kind === "textarea" ? 3 : undefined}
          className="input-belle"
          style={{ width: "100%", fontSize: 14 }}
        />
      </div>
      <div className="workshop-actions" style={{ display: "flex", alignItems: "center", gap: 2 }}>
        <HistoryButton fieldKey={field.key} onRestore={(v) => onChange(field.key, v)} />
        {isOverridden && (
          <button
            type="button"
            onClick={() => onReset(field.key)}
            aria-label="Reset to default"
            title="Reset to default"
            style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 6 }}
          >
            <RotateCcw size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

const ImageField = ({ field, value, local, setLocal, onChange, onReset, isOverridden, busy }) => {
  const [uploading, setUploading] = useState(false);
  const displayUrl = (value && value !== "") ? value : field.default;

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const sig = await api.get("/cloudinary/signature", { params: { resource_type: "image", folder: "site/" } });
      const fd = new FormData();
      fd.append("file", file);
      fd.append("api_key", sig.data.api_key);
      fd.append("timestamp", sig.data.timestamp);
      fd.append("signature", sig.data.signature);
      fd.append("folder", sig.data.folder);
      const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.data.cloud_name}/image/upload`, { method: "POST", body: fd });
      const data = await res.json();
      if (data.secure_url) {
        setLocal(data.secure_url);
        onChange(field.key, data.secure_url);
        toast.success("Image updated.");
      } else {
        toast.error("Upload failed.");
      }
    } catch {
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="workshop-row" style={{ padding: "14px 0", borderBottom: "1px solid var(--border-default)" }}>
      <label className="workshop-label" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 15, color: "var(--text-primary)" }}>
        {field.label}
        {isOverridden && (
          <span style={{ display: "block", fontSize: 10, color: "var(--brand-accent)", fontFamily: "'Outfit', sans-serif", letterSpacing: "0.15em", textTransform: "uppercase", marginTop: 4 }}>
            Custom
          </span>
        )}
      </label>
      <div className="workshop-input-wrap workshop-image-wrap" style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
        <img
          loading="lazy" decoding="async" src={displayUrl}
          alt=""
          style={{ width: 120, height: 80, objectFit: "cover", border: "1px solid var(--border-default)", flexShrink: 0 }}
        />
        <div style={{ flex: 1, minWidth: 200 }}>
          <label
            className="btn-secondary"
            style={{ cursor: uploading || busy ? "wait" : "pointer", display: "inline-block", fontSize: 13 }}
          >
            {uploading ? "Uploading…" : "Upload image"}
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => handleFile(e.target.files?.[0])}
              disabled={uploading || busy}
              data-testid={`workshop-upload-${field.key}`}
            />
          </label>
          <input
            type="text"
            value={local}
            onChange={(e) => setLocal(e.target.value)}
            onBlur={() => { if (local !== (value ?? "")) onChange(field.key, local); }}
            placeholder="Or paste an image URL…"
            className="input-belle"
            style={{ marginTop: 10, width: "100%", fontSize: 12 }}
            data-testid={`workshop-url-${field.key}`}
          />
        </div>
      </div>
      <div className="workshop-actions" style={{ display: "flex", alignItems: "center", gap: 2 }}>
        <HistoryButton
          fieldKey={field.key}
          onRestore={(v) => { setLocal(v); onChange(field.key, v); }}
        />
        {isOverridden && (
          <button
            type="button"
            onClick={() => { setLocal(""); onReset(field.key); }}
            aria-label="Reset to default"
            title="Reset to default"
            style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 6 }}
          >
            <RotateCcw size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

export const EditorWorkshop = () => {
  const { content, refresh } = useSiteContent();
  const [busy, setBusy] = useState(false);

  const saveOne = async (key, value) => {
    setBusy(true);
    try {
      if (!value || value === "") {
        await api.delete(`/site-content/${encodeURIComponent(key)}`);
      } else {
        await api.put(`/site-content/${encodeURIComponent(key)}`, { value });
      }
      await refresh();
      toast.success("Saved.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  const resetOne = async (key) => {
    setBusy(true);
    try {
      await api.delete(`/site-content/${encodeURIComponent(key)}`);
      await refresh();
      toast.success("Reset to default.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="editors-workshop"
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border-default)",
        padding: "36px 32px",
        marginBottom: 48,
      }}
    >
      <style>{`
        .workshop-row {
          display: grid;
          grid-template-columns: 200px 1fr auto;
          gap: 16px;
          align-items: flex-start;
        }
        .workshop-label { padding-top: 8px; }
        /* Stack the label / input / actions on tablets and phones so the
           upload button and "Reset" icon remain reachable on narrow screens. */
        @media (max-width: 767px) {
          .workshop-row {
            grid-template-columns: 1fr;
            gap: 10px;
          }
          .workshop-label { padding-top: 0; }
          .workshop-actions { justify-content: flex-end; }
        }
      `}</style>
      <div className="overline" style={{ color: "var(--brand-accent)" }}>
        Editor&apos;s Workshop
      </div>
      <h2 className="editorial-h2" style={{ fontSize: 28, marginTop: 8, marginBottom: 10 }}>
        Tend to the salon&apos;s words and images.
      </h2>
      <p className="editorial-body" style={{ fontSize: 14, color: "var(--text-muted)", maxWidth: 640, margin: "0 0 24px 0", lineHeight: 1.6 }}>
        Change a headline. Swap a header photograph. Edit a room&apos;s description.
        Changes save the moment you click outside a field (or upload an image). Every save is
        remembered — open the <History size={12} style={{ display: "inline", verticalAlign: "middle" }} /> clock icon
        next to any field to browse and restore an earlier version. The <RotateCcw size={12} style={{ display: "inline", verticalAlign: "middle" }} /> icon resets to the original.
      </p>

      {FIELD_GROUPS.map((group) => (
        <div key={group.title} style={{ marginTop: 36 }}>
          <div className="overline" style={{ color: "var(--brand-accent)" }}>
            {group.overline}
          </div>
          <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, marginTop: 6, marginBottom: 18 }}>
            {group.title}
          </h3>
          <div>
            {group.fields.map((f) => (
              <FieldRow
                key={f.key}
                field={f}
                value={content[f.key]}
                onChange={saveOne}
                onReset={resetOne}
                busy={busy}
              />
            ))}
          </div>
        </div>
      ))}
    </section>
  );
};

export default EditorWorkshop;
